import React, { useCallback, useState, useEffect, useRef } from "react";
import { downloadCsv, getFleetAdminDisplayName, getFleetCompanyType } from "../common/sharedFunctions";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams,useLocation } from "react-router-dom";
import moment from 'moment';
import { colors } from "../components/Theme/WebTheme";
import AlertDialog from "../components/AlertDialog";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Switch from "@mui/material/Switch";
import { Box, Grid, Typography, } from "@mui/material";
import {MAIN_COLOR} from "../common/sharedFunctions"
import EditIcon from '@mui/icons-material/Edit';
import localization from '../components/Table/Localization';
import { hasUserPermission } from "../common/userPermissions";
import { canDoAction, showActionPermissionDenied } from "common/src/actions/userPermissions";
import AdminDataTable from "../components/AdminDataTable";
import AdminStatusBadge from "../components/AdminStatus";
import { ActionButton, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const truthyStatusValues = ["true", "1", "yes", "active", "approved", "enabled", "on"];
const inactiveStatusValues = ["false", "0", "no", "inactive", "disabled", "off", "blocked", "suspended", "deleted"];

const isTruthyStatus = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") return truthyStatusValues.includes(value.trim().toLowerCase());
  return false;
};

const isInactiveStatus = (value) => {
  if (value === false || value === 0) return true;
  if (typeof value === "string") return inactiveStatusValues.includes(value.trim().toLowerCase());
  return false;
};

const isDriverAccountEnabled = (driver) => {
  if (!driver) return false;
  if (isTruthyStatus(driver.blocked) || isTruthyStatus(driver.suspended) || isTruthyStatus(driver.deleted)) return false;
  return isTruthyStatus(driver.active) && !isInactiveStatus(driver.active);
};

const adminTableResponsiveCss = `
  .garahex-admin-table-responsive,
  .garahex-users-table-responsive,
  .garahex-drivers-table-responsive {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    box-sizing: border-box;
  }
  .garahex-admin-table-responsive .MuiPaper-root,
  .garahex-users-table-responsive .MuiPaper-root,
  .garahex-drivers-table-responsive .MuiPaper-root {
    width: 100%;
    min-width: 980px;
    box-sizing: border-box;
  }
  .garahex-admin-table-responsive table,
  .garahex-users-table-responsive table,
  .garahex-drivers-table-responsive table {
    min-width: 980px;
    border-collapse: collapse;
  }
  .garahex-admin-table-responsive .MuiTableCell-root,
  .garahex-users-table-responsive .MuiTableCell-root,
  .garahex-drivers-table-responsive .MuiTableCell-root {
    font-size: 12px !important;
    padding: 8px 10px !important;
    white-space: nowrap;
  }
  .garahex-admin-table-responsive .MuiToolbar-root,
  .garahex-users-table-responsive .MuiToolbar-root,
  .garahex-drivers-table-responsive .MuiToolbar-root {
    min-width: 980px;
  }
`;

export default function Users() {
  const navigate = useNavigate();
  const {id} = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editUser,
    deleteUser,
    fetchUsersOnce,
    sendFleetAdminDriverInvite,
  } = api;
  const [data, setData] = useState([]);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const auth = useSelector((state) => state.auth);
  const isFleetAdmin = auth && auth.profile && String(auth.profile.usertype).toLowerCase() === "fleetadmin";
  const settings = useSelector((state) => state.settingsdata.settings);
  const dispatch = useDispatch();
  const loaded = useRef(false);
  const [role, setRole] = useState(null);
  const [fleetAdminsObj, setFleetAdminsObj] = useState();
  const [sortedData, SetSortedData] = useState([]);
  const {state} = useLocation()
  const [currentPage,setCurrentPage] = useState(null)
  const [inviteBusyDriverId, setInviteBusyDriverId] = useState(null);

  useEffect(()=>{
    setCurrentPage(state?.pageNo)
  },[state])
  
  const HandalePageChange = (page)=>{
    setCurrentPage(page)
  }

  const getFleetAdminOptionText = useCallback((user) => (
    getFleetAdminDisplayName(user) +
    " - " +
    getFleetCompanyType(user) +
    " (" +
    (settings.AllowCriticalEditsAdmin ? user.mobile : t("hidden_demo")) +
    ") " +
    (settings.AllowCriticalEditsAdmin ? user.email : t("hidden_demo"))
  ), [settings.AllowCriticalEditsAdmin, t]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (staticusers) {
      if (role === "admin") {
        let arr = staticusers.filter((user) => user.usertype === "fleetadmin");
        let obj = {};
        let arr2 = [];
        for (let i = 0; i < arr.length; i++) {
          let user = arr[i];
          arr2.push({
            id: user.id,
            desc: getFleetAdminOptionText(user),
          });
          obj[user.id] = getFleetAdminOptionText(user);
        }
        setFleetAdminsObj(obj);
      }
      setTimeout(() => {
        setData(
          staticusers.filter(
            (user) => {
              if (user.usertype !== "driver") return false;
              if (auth.profile.usertype === "admin") return true;
              if (auth.profile.usertype !== "fleetadmin") return false;
              return user.fleetadmin === auth.profile.uid ||
                user.fleetadminUid === auth.profile.uid ||
                user.createdBy === auth.profile.uid;
            }
          )
        );
      }, 1000);
    } else {
      setData([]);
    }
    loaded.current = true;
  }, [
    staticusers,
    auth.profile.usertype,
    auth.profile.uid,
    settings.AllowCriticalEditsAdmin,
    role,
    t,
    getFleetAdminOptionText
  ]);

  useEffect(()=>{
    if(data){
      SetSortedData(data.sort((a,b)=>(moment(b.createdAt) - moment(a.createdAt))))
    }
  },[data])

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

 
  const handelApproved = (rowData) => {
        if (!canEditUser) {
            showActionPermissionDenied();
            return rowData;
        }
    const nextApproved = !isTruthyStatus(rowData.approved);
    const updatedUser = {
      ...rowData,
      approved: nextApproved,
      adminApprovedTrue: nextApproved,
      active: nextApproved
    };
    if (!nextApproved) {
      updatedUser.driverActiveStatus = false;
    }

    dispatch(editUser(updatedUser.id, updatedUser));
    dispatch(fetchUsersOnce());
    return updatedUser;
  };
  const handelDriverActiveStatus = (rowData) => {
    if (!canManageDriverActiveStatus(rowData)) {
      showActionPermissionDenied();
      return rowData;
    }
    const nextActive = !isDriverAccountEnabled(rowData);
    const updatedUser = {
      ...rowData,
      active: nextActive,
      driverActiveStatus: false,
    };

    dispatch(editUser(updatedUser.id, updatedUser))
      .then(() => {
        setData((currentData) => currentData.map((item) => item.id === updatedUser.id ? updatedUser : item));
        SetSortedData((currentData) => currentData.map((item) => item.id === updatedUser.id ? updatedUser : item));
        setCommonAlert({
          open: true,
          msg: nextActive ? "Driver account activated." : "Driver account deactivated."
        });
        dispatch(fetchUsersOnce());
      })
      .catch((error) => {
        setCommonAlert({
          open: true,
          msg: error && error.message ? error.message : "Unable to update driver active status."
        });
      });
    return updatedUser;
  };

  const ownsDriver = (driver) => driver && (
    driver.fleetadmin === auth.profile.uid ||
    driver.fleetadminUid === auth.profile.uid ||
    driver.createdBy === auth.profile.uid
  );

  const canInviteDriver = (driver) => auth.profile.usertype === "admin" ||
    (auth.profile.usertype === "fleetadmin" && ownsDriver(driver));

  const canManageDriverActiveStatus = (driver) => auth.profile.usertype === "admin" ||
    (auth.profile.usertype === "fleetadmin" && ownsDriver(driver));

  const handleDriverInvite = async (driver) => {
    if (!canInviteDriver(driver)) {
      setCommonAlert({ open: true, msg: "Driver does not belong to this Fleet Admin." });
      return;
    }
    setInviteBusyDriverId(driver.id);
    try {
      const result = await dispatch(sendFleetAdminDriverInvite(driver.id));
      setCommonAlert({
        open: true,
        msg: result.status === "invite_resent"
          ? "Driver login invitation resent successfully."
          : "Driver login invitation sent successfully."
      });
    } catch (error) {
      setCommonAlert({
        open: true,
        msg: error && error.message ? error.message : "Failed to send the driver invitation."
      });
    } finally {
      setInviteBusyDriverId(null);
    }
  };

  const getDriverFleetAdmin = (driver) => {
    const ownerId = driver && (driver.fleetadmin || driver.fleetadminUid || driver.createdBy);
    if (!ownerId || !Array.isArray(staticusers)) return null;
    return staticusers.find(user => user && user.usertype === "fleetadmin" &&
      (user.id === ownerId || user.uid === ownerId)) || null;
  };

  const columns = [
    { title: t('createdAt'), field: 'createdAt', editable:'never', defaultSort:'desc',render: rowData => rowData.createdAt? moment(rowData.createdAt).format('lll'):null,},

    { title: t("first_name"), field: "firstName", initialEditValue: "" },
    { title: t("last_name"), field: "lastName", initialEditValue: "" },
    role === "admin" && {
      title: "Fleet Admin First Name",
      field: "fleetAdminFirstName",
      editable: "never",
      render: rowData => {
        const owner = getDriverFleetAdmin(rowData);
        return owner ? owner.firstName || "N/A" : "N/A";
      }
    },
    role === "admin" && {
      title: "Fleet Admin Last Name",
      field: "fleetAdminLastName",
      editable: "never",
      render: rowData => {
        const owner = getDriverFleetAdmin(rowData);
        return owner ? owner.lastName || "N/A" : "N/A";
      }
    },
    role === "admin" && {
      title: "Fleet Admin Company Name",
      field: "fleetAdminCompanyName",
      editable: "never",
      render: rowData => {
        const owner = getDriverFleetAdmin(rowData);
        return owner ? owner.companyName || "N/A" : "N/A";
      }
    },
    {
      title: t("mobile"),
      field: "mobile",
      editable: "onAdd",
      render: (rowData) =>
        settings.AllowCriticalEditsAdmin ? rowData.mobile : t("hidden_demo"),
    },
    {
      title: t("email"),
      field: "email",
      editable: "onAdd",
      render: (rowData) =>
        settings.AllowCriticalEditsAdmin ? rowData.email : t("hidden_demo"),
      headerStyle: { textAlign: "center" },
    },
    {
      title: t("profile_image"),
      field: "profile_image",
      render: (rowData) =>
        rowData.profile_image ? (
          <img
            alt="Profile"
            src={rowData.profile_image}
            style={{ width: 40, height: 40, borderRadius: "50%" }}
          />
        ) : (
          <AccountCircleIcon sx={{ fontSize: 40 }} />
        ),
      editable: "never",
    },
    {
      title: t("account_approve"),
      field: "approved",
      type: "boolean",
      render: (rowData) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AdminStatusBadge status={isTruthyStatus(rowData.approved) ? "APPROVED" : "PENDING"} />
          <Switch
            disabled={!settings.AllowCriticalEditsAdmin}
            checked={isTruthyStatus(rowData.approved)}
            onChange={() => handelApproved(rowData)}
          />
        </Box>
      ),
    },
    {
      title: t("driver_active"),
      field: "active",
      editable: "never",
      type: "boolean",
      render: (rowData) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AdminStatusBadge status={isDriverAccountEnabled(rowData) ? "ACTIVE" : "DISABLED"} />
          <Switch
            disabled={!canManageDriverActiveStatus(rowData)}
            checked={isDriverAccountEnabled(rowData)}
            onChange={() => handelDriverActiveStatus(rowData)}
          />
        </Box>
      ),
    },
    {
      title: t("queue"),
      field: "queue",
      editable: "never",
      type: "boolean",
      initialEditValue: true,
      render: (rowData) => (
        <Switch
          disabled
          checked={isTruthyStatus(rowData.queue)}
          onChange={() => console.log(rowData.queue)}
        />
      ),
    },
    {
      title: "Login Provisioning",
      field: "authProvisioningStatus",
      editable: "never",
      render: (rowData) => {
        if (rowData.authProvisioningStatus === "invited") return <AdminStatusBadge status="INVITED">Invited</AdminStatusBadge>;
        if (rowData.authProvisioningStatus === "invite_resent") return <AdminStatusBadge status="INVITED">Invite resent</AdminStatusBadge>;
        if (rowData.authProvisioningStatus === "failed") return <AdminStatusBadge status="FAILED">Failed</AdminStatusBadge>;
        return <AdminStatusBadge status="PENDING">Not invited</AdminStatusBadge>;
      },
    }
  ].filter(Boolean);

  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };

  const userActionAllowed = (action) => canDoAction({
        role: auth && auth.profile && auth.profile.usertype,
        moduleKey: "users",
        action,
        accessPermissions: settings && settings.accessPermissions,
        fallback: hasUserPermission(settings, "driver", action)
    });
    const canAddUser = userActionAllowed("add");
    const canEditUser = userActionAllowed("edit");
    const canViewUser = userActionAllowed("view");
    const canDeleteUser = userActionAllowed("delete");
  const tableActions = [
    canAddUser && {
      icon: "add",
      tooltip: t("add_driver"),
      isFreeAction: true,
      onClick: (event) => navigate("/users/edituser/driver",{state:{pageNo:currentPage}}),
    },
    canEditUser && ((rowData) => ({
      tooltip: t("edit"),
      icon: () => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          marginRight:0
        }}
      >
        <EditIcon />
      </div>
    ),
    onClick: (event, rowData) =>{
      navigate(`/users/edituser/driver/${rowData.id}`,{state:{pageNo:currentPage}})
    }
  })),
    ((rowData) => ({
      tooltip: rowData.authProvisioningStatus === "invited" || rowData.authProvisioningStatus === "invite_resent"
        ? "Resend driver login invitation"
        : "Send driver login invitation",
      hidden: !canInviteDriver(rowData),
      disabled: inviteBusyDriverId === rowData.id,
      icon: () => (
        <div style={{ padding: "8px 10px", backgroundColor: MAIN_COLOR, borderRadius: 5 }}>
          <Typography style={{ color: colors.LandingPage_Background, whiteSpace: "nowrap" }}>
            {inviteBusyDriverId === rowData.id
              ? "Sending..."
              : rowData.authProvisioningStatus === "invited" || rowData.authProvisioningStatus === "invite_resent"
                ? "Resend Invite"
                : "Send Invite"}
          </Typography>
        </div>
      ),
      onClick: () => handleDriverInvite(rowData)
    })),
    canViewUser && {
      icon: "info",
      tooltip: t("profile_page_subtitle"),
      onClick: (event, rowData) => {
        navigate(`/users/driverdetails/${rowData.id}`,{state:{pageNo:currentPage}});
      },
    },
    canViewUser && ((rowData) => ({
      icon: () => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            padding: 10,
            backgroundColor: MAIN_COLOR,
            borderRadius: 5,
          }}
        >
          <Typography style={{ color: colors.LandingPage_Background }}>
            {t("documents")}
          </Typography>
        </div>
      ),
      tooltip: t("documents"),
      onClick: () => navigate(`/users/userdocuments/${id}/${rowData.id}`,{state:{pageNo:currentPage}}),
    }))
  ].filter(Boolean);

  const [selectedRow, setSelectedRow] = useState(null);

  return !loaded.current ? (
    <CircularLoading />
  ) : (
    <Box style={{ backgroundColor: colors.LandingPage_Background, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <style>{adminTableResponsiveCss}</style>
      {isFleetAdmin ? (
        <Box style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <ActionButton
            onClick={() => navigate("/users/edituser/driver", { state: { pageNo: currentPage } })}
          >
            ADD DRIVER
          </ActionButton>
        </Box>
      ) : null}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 110 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Drivers</Typography>
            <Typography sx={{ color: adminTokens.colors.text, fontSize: "1.8rem", fontWeight: 950 }}>{sortedData.length}</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 110 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Active Drivers</Typography>
            <Typography sx={{ color: adminTokens.colors.success, fontSize: "1.8rem", fontWeight: 950 }}>{sortedData.filter((item) => isDriverAccountEnabled(item)).length}</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 110 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Invited</Typography>
            <Typography sx={{ color: adminTokens.colors.purple, fontSize: "1.8rem", fontWeight: 950 }}>{sortedData.filter((item) => item.authProvisioningStatus === "invited" || item.authProvisioningStatus === "invite_resent").length}</Typography>
          </SurfaceCard>
        </Grid>
      </Grid>
      <SurfaceCard>
        <SectionHeader
          title={t("drivers_title")}
          description="Driver accounts, ownership, login invitations, documents, and operational status use the existing user-management workflow."
          actions={<StatusBadge tone="info">{`${sortedData.length} records`}</StatusBadge>}
        />
        <AdminDataTable
          title={t("drivers_title")}
          columns={columns}
          onChangePage={(page)=>HandalePageChange(page)}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
          }}
          data={sortedData}
          emptyTitle="No drivers found"
          emptyDescription="No driver records match the current account scope."
          onRowClick={(evt, selectedRow) =>
            setSelectedRow(selectedRow.tableData.id)
          }
          options={{
            initialPage:state?.pageNo,
            exportCsv: (columns, data) => {
              let hArray = [];
              const headerRow = columns.map((col) => {
                if (typeof col.title === "object") {
                  return col.title.props.text;
                }
                hArray.push(col.field);
                return col.title;
              });
              const dataRows = data.map(({ tableData, ...row }) => {
                row.createdAt =
                  new Date(row.createdAt).toLocaleDateString() +
                  " " +
                  new Date(row.createdAt).toLocaleTimeString();
                row.fleetadmin = row.fleetadmin
                  ? fleetAdminsObj[row.fleetadmin]
                  : "";
                let dArr = [];
                for (let i = 0; i < hArray.length; i++) {
                  dArr.push(row[hArray[i]]);
                }
                return Object.values(dArr);
              });
              const { exportDelimiter } = ",";
              const delimiter = exportDelimiter ? exportDelimiter : ",";
              const csvContent = [headerRow, ...dataRows]
                .map((e) => e.join(delimiter))
                .join("\n");
              const csvFileName = "download.csv";
              downloadCsv(csvContent, csvFileName);
            },
            exportButton: {
              csv: settings.AllowCriticalEditsAdmin,
              pdf: false,
            },
            maxColumnSort: "all_columns",
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData.id ? "rgba(11, 124, 255, 0.09)" :colors.WHITE
            }),
          }}
          localization={localization(t)}
          editable={canDeleteUser ? {
            onRowDelete: (oldData) =>
              settings.AllowCriticalEditsAdmin
                ? new Promise((resolve) => {
                    setTimeout(() => {
                      resolve();
                      dispatch(deleteUser(oldData.id));
                      dispatch(fetchUsersOnce());
                    }, 600);
                  })
                : new Promise((resolve) => {
                    setTimeout(() => {
                      resolve();
                      alert(t("demo_mode"));
                    }, 600);
                  }),
          } : {}}
          actions={tableActions}
        />
      </SurfaceCard>
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
    </Box>
  );
}
